<template>
  <div class="container section-search-list pb-6">
    <div class="search-form mt-4">
      <div class="row">
        <div class="col">
          <h1 v-if="!maxDevice('md')">Search</h1>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <tml-form form-name="search-page-search-form">
            <tml-input
              v-model="searchTerm"
              enterkeyhint="search"
              class="mb-0"
              field-name="search-input"
              placeholder="Search"
              :icon="faSearch"
              :right-icon="searchTerm ? faCircleArrowRight : null"
              :loading="loading"
              :focus-input="true"
              v-on="
                // Prevents search icon from being clickable when input is empty
                searchTerm
                  ? {
                      leftIconClick: applySearchTerm.bind(null, searchTerm),
                      rightIconClick: applySearchTerm.bind(null, searchTerm),
                    }
                  : {}
              "
              @submit="applySearchTerm"
              @new-input="onDebounceInput($event)"
            />
          </tml-form>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <tml-grid
            v-if="searched.length"
            class="search-results pt-4"
            :breakpoints="{xs: 2, sm: 3, md: 4}"
          >
            <tml-new-pod
              v-for="(product, index) in searched"
              :key="index"
              :title="product.name"
              :title-size="maxDevice('md') ? 0 : 1"
              :image="product.image ? formatUrl(product.image) : null"
              :href="getProductHref(product)"
              :price="product.price"
              :sale-price="product.salePrice"
              :image-src-set="[
                {width: 320, height: 320},
                {width: 480, height: 480},
                {width: 640, height: 640},
              ]"
            />
          </tml-grid>
          <p v-else-if="noResults" class="mt-6">
            Sorry, no items were found matching "<b>{{ lastSearch }}</b
            >".
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {useGtm} from '@teemill/common/services';
import {formatUrl, maxDevice} from '@teemill/common/helpers';
import {faSearch, faCircleArrowRight} from '@fortawesome/pro-light-svg-icons';
import {computed} from 'vue';
import {useHead} from '@vueuse/head';

import debounce from 'lodash/debounce';
import {splitTests} from '@teemill/common/classes';

export default {
  name: 'SearchPage',

  data() {
    return {
      /**
       * The current search term
       */
      searchTerm: null,

      /**
       * The last search that was performed
       */
      lastSearch: null,

      /**
       * The list of items returned in the query
       */
      searched: [],

      faSearch,
      faCircleArrowRight,

      loading: false,
      noResults: false,

      onDebounceInput: () => {},
    };
  },

  watch: {
    $route: {
      immediate: true,
      deep: true,
      handler(route) {
        this.searchTerm =
          route.query['search'] || route.query['search-term'] || '';

        if (this.searchTerm) {
          this.search(this.searchTerm);
        }
      },
    },
  },

  created() {
    useHead(
      computed(() => ({
        title: $store.state.title,
      }))
    );
  },

  methods: {
    maxDevice,
    debounce,

    applySearchTerm(term) {
      setTimeout(() => {
        this.$router.push({
          query: {
            search: term,
          },
        });
      });
    },

    getProductHref(product) {
      if (product.color) {
        return `/product/${product.urlName}/${encodeURIComponent(
          product.color
        )}/`;
      }

      return `/product/${product.urlName}/`;
    },

    async search(term) {
      if (term !== this.lastSearch) {
        this.loading = true;
        this.noResults = false;

        let version = 'keyword';

        if (this.$store.state.subdomain.productEmbeddingVersion) {
          version =
            $store.getters['paymentProcessor/paymentProcessorType'] ===
              'direct' ||
            splitTests.isActiveVariation('product_vector_search_2', 'vector')
              ? 'vector'
              : 'keyword';
        }

        this.axios
          .get(
            formatUrl(
              `/omnis/v3/frontend/product/search/?search-term=${term}&version=${version}`
            )
          )
          .success(data => {
            this.searched = data;

            if (!this.searched || !this.searched.length) {
              this.noResults = true;
            }
          })
          .oops()
          .finally(() => {
            this.loading = false;
          });

        this.lastSearch = term;

        /**
         * Log the search terms to GA
         */

        useGtm().trackEvent({
          event: 'OnSearch',
          category: 'Subdomains',
          action: 'Search',
          value: term,
        });
      }
    },

    formatUrl,
  },
};
</script>

<style lang="scss" scoped>
.search-form {
  background-color: var(--tml-page-background-color);

  .search-results {
    background-color: var(--tml-page-background-color) !important;
  }
}
</style>
